import { useCallback, useState } from "react";

import { duplicatePermissionProfilePreset } from "@/app/access-presets/data_sources/services";
import type { APIPermissionProfilePreset } from "@/core/data_sources/models/APIPermissionProfilePreset";
import { AppStore } from "@/core/data_sources/store/app";
import { Button } from "@/core/ui/atoms";
import { adminInputClasses, Banner } from "@/core/ui/molecules";
import { ErrorResponse } from "@/core/utils/request";

export function DuplicatePresetDialog({
  onClose,
  onSaved,
  preset,
}: {
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly preset: APIPermissionProfilePreset;
}) {
  const source = preset.getData();
  const [presetCode, setPresetCode] = useState(
    `${source.preset_code ?? ""}_copy`,
  );
  const [name, setName] = useState(`${source.name ?? ""} (Copy)`);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    const errs: string[] = [];
    if (!name.trim()) errs.push("Name is required");
    if (!presetCode.trim()) errs.push("Preset Code is required");
    else if (!/^[a-z][a-z0-9_]*$/.test(presetCode))
      errs.push("Preset Code must be lowercase snake_case (a-z, 0-9, _)");
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    setErrors([]);
    setSaving(true);
    try {
      const resp = await duplicatePermissionProfilePreset(source.id!, {
        preset_code: presetCode,
        name,
      }).response;
      if ((resp.data as ErrorResponse).error) throw resp.data;
      AppStore.instance.actions.notification(
        0,
        `Preset "${name}" duplicated`,
        2500,
        true,
      );
      onSaved();
      onClose();
    } catch (err) {
      const e = err as ErrorResponse;
      if (e.status === 409)
        setErrors(["A preset with this code already exists."]);
      else setErrors([e.error?.message || "Duplicate failed"]);
      setSaving(false);
    }
  }, [name, presetCode, source.id, onSaved, onClose]);

  return (
    <div className="flex max-h-[calc(100vh-7.5rem)] w-[560px] max-w-[96vw] flex-col overflow-hidden rounded-dialog bg-surface shadow-dialog">
      <header className="border-b border-border px-5 py-5">
        <h2 className="text-lg font-extrabold text-ink">
          Duplicate Permission Profile Preset
        </h2>
        <p className="mt-1 text-[13px] text-ink-muted">
          Creates a new preset (v1, active) from “{source.name}”.
        </p>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <Banner>
          The new preset starts at version 1 with status active. References in
          plans keep pointing to the original preset.
        </Banner>
        <label className="mt-3 block">
          <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
            New preset name <span className="text-red-500">*</span>
          </span>
          <input
            className={adminInputClasses}
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
        </label>
        <label className="mt-3 block">
          <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
            New preset code <span className="text-red-500">*</span>
          </span>
          <input
            className={`${adminInputClasses} font-mono`}
            onChange={(e) =>
              setPresetCode(
                e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
              )
            }
            value={presetCode}
          />
        </label>
        {errors.length > 0 ? (
          <Banner tone="error">
            <ul className="ml-4 list-disc">
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </Banner>
        ) : null}
      </div>
      <footer className="flex justify-end gap-2 border-t border-border bg-panel-muted px-5 py-3.5">
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={saving} onClick={handleSave} variant="primary">
          Duplicate
        </Button>
      </footer>
    </div>
  );
}
